import { useMutation } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useAuthStore } from '../store/authStore'

interface LoginPayload {
  email: string
  password: string
  tenant_id?: string
}

interface LoginResponse {
  access_token: string
  refresh_token: string
  expires_in?: number
}

interface JWTPayload {
  user_id?: string
  tenant_id?: string
  email?: string
  name?: string
  roles?: string[]
  permissions?: string[]
  exp?: number
}

function decodeJWT(token: string): JWTPayload {
  try {
    const part = token.split('.')[1]
    if (!part) return {}
    const padded = part.replace(/-/g, '+').replace(/_/g, '/')
    const json = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4))
    return JSON.parse(json)
  } catch {
    return {}
  }
}

function completeLogin(data: { access_token: string; refresh_token: string; user?: { id: string; name: string; email: string; tenant_id: string } }) {
  const claims = decodeJWT(data.access_token)
  const setAuth = useAuthStore.getState().setAuth
  setAuth(data.access_token, data.refresh_token, {
    id: data.user?.id ?? claims.user_id ?? '',
    name: data.user?.name ?? claims.name ?? '',
    email: data.user?.email ?? claims.email ?? '',
    tenant_id: data.user?.tenant_id ?? claims.tenant_id ?? '',
    roles: claims.roles ?? [],
    permissions: claims.permissions ?? [],
  })
}

export function useLogin() {
  return useMutation({
    mutationFn: (data: LoginPayload) =>
      api.post<LoginResponse>('/auth/login', data).then((r) => r.data),
    onSuccess: (data) => {
      completeLogin(data)
    },
  })
}

// ── Password change (authenticated user) ──────────────────────

interface ChangePasswordPayload {
  current_password: string
  new_password: string
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (data: ChangePasswordPayload) =>
      api.put<{ message: string }>('/auth/password', data).then((r) => r.data),
  })
}
